import $ from 'jquery';
import '../web/djangoCSRF/djangoCSRF';
import { encodeRequestPayload } from './resourceHelper';

const DeferredResourceAPI = {
  _path: function () {
    var args = Array.prototype.slice.call(arguments);
    return '/json/' + args.join('/') + '/';
  },

  /**
   * How to use requests or multi-requests.
   *
   * a) RESOURCE.read('resourceName', resourceID, 'getSomething', { arg1: true })
   *        .done(callback)
   *
   * b) RESOURCE.read('resourceName', resourceID, [ 'getSomething', 'getSomethingElse', 'getOneMoreThing'])
   *        .done(callback)
   *
   * c)
   * var methodWithArgs = {
   *     'method': 'getSpecificThing',
   *     'args'  : {
   *         createdBy: 'twig'
   *     }
   * };
   * RESOURCE.read('resourceName', resourceID, [ 'getSomething', methodWithArgs])
   *     .done(callback)
   *
   * d)
   * var argsForMultiRequest = {
   *     'getSpecificThing': {
   *         createdBy: 'twig'
   *     }
   * };
   *
   * RESOURCE.read('resourceName', resourceID, [ 'getSomething', 'getSpecificThing'], argsForMultiRequest)
   *     .done(callback)
   */
  _send: function (type, url, data, options, files) {
    var key;
    var deferred = $.Deferred();

    data = encodeRequestPayload(data);

    var ajaxOptions = {
      url: url,
      type: type,
      data: data,
      dataType: 'json',
    };

    options = options || {};

    var succinctError = options.succinctError;
    delete options.succinctError;
    $.extend(ajaxOptions, options);

    if (files) {
      // Create a formdata object and add the files
      var formdata = new FormData();
      Object.keys(files).forEach(function (key) {
        var value = files[key];
        formdata.append(key, value);
      });
      Object.keys(data).forEach(function (key) {
        var value = data[key];
        formdata.append(key, value);
      });

      var fileOptions = {
        processData: false, // Don't process the files
        contentType: false, // Set content type to false as jQuery will tell the server its a query string request
        data: formdata,
      };

      $.extend(ajaxOptions, fileOptions);
    }

    var jqXHR = $.ajax(ajaxOptions)
      .done(function (response) {
        var result;
        if (!response.hasOwnProperty('error') && response.success !== false) {
          if (data['methodsJSON']) {
            result = [];

            $.each(response.result, function (i, r) {
              result.push(r.result);
            });
          } else {
            result = response.result;
          }

          if (response.id) {
            deferred.resolve(result, response.id, response);
          } else {
            deferred.resolve(result, response);
          }
        } else {
          var error =
            response && response.hasOwnProperty('error')
              ? result.error
              : 'Error: Request Failed';
          deferred.reject('Error ' + error + ': ' + response.reason, response);
        }
      })
      .fail(function (response, textStatus, errorThrown) {
        var parsed_result;

        if (response.hasOwnProperty('responseJSON')) {
          response = response.responseJSON;
        }

        if (textStatus === 'timeout') {
          deferred.reject('Request timed out.');
          //          } else if (textStatus === "error") {
          //            deferred.reject("Request timed out.");
        } else if (textStatus === 'abort') {
          deferred.reject('Request aborted.');
        } else if (textStatus === 'parsererror') {
          deferred.reject('Error parsing response.');
        } else if (response === undefined) {
          deferred.reject('No response');
        } else if (
          (response.hasOwnProperty('error') &&
            response.error.constructor === String) ||
          response.success === false
        ) {
          deferred.reject(
            succinctError
              ? response.reason
              : 'Error ' + response.error + ': ' + response.reason,
            response
          );
        } else {
          try {
            parsed_result = JSON.parse(response.responseText);
          } catch (err) {
            parsed_result = {
              error: response.status + ': ' + response.statusText,
            };
          }
          deferred.reject('Error: ' + parsed_result.error, response);
          if (parsed_result.errorCode === 4) {
            window.location.reload();
          }
        }
      });

    deferred.abort = jqXHR.abort;

    return deferred;
  },

  create: function (resource, data, methods, options, files) {
    var RESOURCE = this;
    var url;

    if (Object.prototype.toString.call(methods) === '[object Array]') {
      url = RESOURCE._path(resource, 'multi/create');
      return RESOURCE._send(
        'POST',
        url,
        {
          creationData: JSON.stringify(data),
          methodsJSON: JSON.stringify(methods),
        },
        options,
        files
      );
    } else {
      options = methods; // missing the methods arg
      url = RESOURCE._path(resource, 'create');
      return RESOURCE._send('POST', url, data, options, files);
    }
  },

  read: function (resource, resourceID, method, args, options) {
    var RESOURCE = this;
    var url;

    if (Object.prototype.toString.call(method) === '[object Array]') {
      url = RESOURCE._path(resource, 'multi/read', resourceID);
      return RESOURCE._send(
        'GET',
        url,
        { methodsJSON: JSON.stringify(method) },
        args
      );
    } else {
      url = RESOURCE._path(resource, 'read', method, resourceID);
      return RESOURCE._send('GET', url, args, options);
    }
  },

  update: function (resource, resourceID, method, args, options) {
    var RESOURCE = this;
    var url;

    if (Object.prototype.toString.call(method) === '[object Array]') {
      url = RESOURCE._path(resource, 'multi/update', resourceID);
      return RESOURCE._send(
        'POST',
        url,
        { methodsJSON: JSON.stringify(method) },
        args
      );
    } else {
      url = RESOURCE._path(resource, 'update', method, resourceID);
      return RESOURCE._send('POST', url, args, options);
    }
  },

  delete: function (resource, resourceID, options) {
    var RESOURCE = this;
    var url = RESOURCE._path(resource, 'delete', resourceID);
    return RESOURCE._send('POST', url, {}, options);
  },

  lookup: function (resource, method, args, options) {
    var RESOURCE = this;
    var url = RESOURCE._path(resource, 'lookup', method);
    return RESOURCE._send('GET', url, args, options);
  },

  execute: function (resource, method, args, options) {
    var RESOURCE = this;
    var url = RESOURCE._path(resource, 'execute', method);
    return RESOURCE._send('POST', url, args, options);
  },
};

export default DeferredResourceAPI;

const apiErrorToError = (message, response) => {
  // errors only have a message field. Sometimes we want the raw response
  // NOTE: only direct callers of the resource API should know about the
  // response field.
  const error = Error(message);
  error.response = response;
  return error;
};

const wrapApiError = (reject) => (result, response) => {
  reject(apiErrorToError(result, response));
};

export const ResourceApi = {
  create: (...args) =>
    new Promise((resolve, reject) =>
      DeferredResourceAPI.create(...args).then(resolve, wrapApiError(reject))
    ),
  read: (...args) =>
    new Promise((resolve, reject) =>
      DeferredResourceAPI.read(...args).then(resolve, wrapApiError(reject))
    ),
  update: (...args) =>
    new Promise((resolve, reject) =>
      DeferredResourceAPI.update(...args).then(resolve, wrapApiError(reject))
    ),
  delete: (...args) =>
    new Promise((resolve, reject) =>
      DeferredResourceAPI.delete(...args).then(resolve, wrapApiError(reject))
    ),
  lookup: (...args) =>
    new Promise((resolve, reject) =>
      DeferredResourceAPI.lookup(...args).then(resolve, wrapApiError(reject))
    ),
  execute: (...args) =>
    new Promise((resolve, reject) =>
      DeferredResourceAPI.execute(...args).then(resolve, wrapApiError(reject))
    ),
};
